import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { DocsChatMessage, DocsHubItem } from './models';

declare const rootUrl: string;
declare const marked: { parse: (md: string) => string };
declare const signalR: any;

interface Props {
  onClose: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

const SESSION_EXPIRED_ERROR = 'Sessie verlopen';
const allowedExtensions = ['pdf', 'docx'];
const maxFileSize = 10 * 1024 * 1024;

function stripMarkdownFences(text: string): string {
  return text.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
}

const controllerLabels: Record<string, string> = {
  Home: 'Home',
  Application: 'Applicatie',
  ApplicationReport: 'Rapportage',
  ApplicationPayment: 'Betaling',
  ApplicationLoan: 'Lening',
  ApplicationRequest: 'Verzoek',
  ApplicationDecision: 'Besluit',
  ApplicationGrantedBudget: 'Toegekend budget',
  ApplicationReleasedBudget: 'Vrijgegeven budget',
  ApplicationRemark: 'Opmerking',
  ApplicationMetadata: 'Metadata',
  ApplicationInvolvedParty: 'Betrokken partij',
  ApplicationSuspendedDecision: 'Opgeschort besluit',
  ApplicationDepot: 'Depot',
  Organization: 'Organisatie',
  Contact: 'Contact',
  Meeting: 'Vergadering',
  MeetingAgendaItem: 'Agendapunt',
  Task: 'Taak',
  Message: 'Bericht',
  MessageOverview: 'Berichtenoverzicht',
  Payment: 'Betaling',
  PaymentFile: 'Betalingsbestand',
  Report: 'Rapportage',
  Request: 'Verzoek',
  Loan: 'Lening',
  LoanRepayment: 'Aflossing',
  LoanRepaymentBatch: 'Aflossingsbatch',
  LoanValuation: 'Waardering',
  User: 'Gebruiker',
  UserGroup: 'Gebruikersgroep',
  Role: 'Rol',
  Search: 'Zoeken',
  Archive: 'Archief',
  Admin: 'Beheer',
  Settings: 'Instellingen',
  FormDefinition: 'Formulierdefinitie',
  Funding: 'Financiering',
  Docs: 'Documentatie',
  Retention: 'Retentie',
  DashboardDefinition: 'Dashboard',
};

const actionLabels: Record<string, string> = {
  Index: 'Overzicht',
  Overview: 'Overzicht',
  Edit: 'Bewerken',
  Create: 'Aanmaken',
  Details: 'Details',
};

function getCulture(): string {
  return document.documentElement.lang || 'nl';
}

function getPageContext(): string | null {
  const path = window.location.pathname.replace(/^\/+/, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const controller = segments[0];
  const action = segments.length > 1 && !/^\d+$/.test(segments[1]) ? segments[1] : null;

  const controllerLabel = controllerLabels[controller] ?? controller;
  const actionLabel = action ? (actionLabels[action] ?? action) : null;

  return actionLabel ? `${controllerLabel} - ${actionLabel}` : controllerLabel;
}

export const DocsChatConversation: React.FC<Props> = ({ onClose, isFullscreen, onToggleFullscreen }) => {
  const [messages, setMessages] = useState<DocsChatMessage[]>(() => {
    try {
      const stored = sessionStorage.getItem('docs-chat-messages');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingType, setStreamingType] = useState<'markdown' | 'formJson'>('markdown');

  // File attachment state
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form generation tracking (persisted in sessionStorage)
  const [sessionId, setSessionId] = useState<string | null>(() => sessionStorage.getItem('docs-chat-sessionId'));
  const [currentFormJson, setCurrentFormJson] = useState<string | null>(() => sessionStorage.getItem('docs-chat-formJson'));

  const lastUploadedFileRef = useRef<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<any>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const questionInputRef = useRef<HTMLInputElement>(null);

  const copyIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
  const downloadIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
  const checkIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

  useEffect(() => {
    sessionStorage.setItem('docs-chat-messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (sessionId) sessionStorage.setItem('docs-chat-sessionId', sessionId);
    else sessionStorage.removeItem('docs-chat-sessionId');
  }, [sessionId]);

  useEffect(() => {
    if (currentFormJson) sessionStorage.setItem('docs-chat-formJson', currentFormJson);
    else sessionStorage.removeItem('docs-chat-formJson');
  }, [currentFormJson]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${rootUrl}/docsHub`)
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;
    connection.start().catch((err: Error) => console.error('DocsHub connection error:', err));

    return () => {
      connection.stop();
    };
  }, []);

  useEffect(() => {
    questionInputRef.current?.focus();
  }, []);

  const isInitialRender = useRef(true);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isInitialRender.current ? 'instant' : 'smooth' });
    isInitialRender.current = false;
  }, [messages, streamingContent]);

  // Copy/download button handler for code blocks (delegated)
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const getCodeText = (btn: HTMLElement) => {
      const wrapper = btn.closest('.docs-chat-code-wrapper');
      const pre = wrapper?.querySelector('pre');
      if (!pre) return '';
      const code = pre.querySelector('code');
      return (code ?? pre).textContent ?? '';
    };
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const copyBtn = target.closest('.docs-chat-copy-btn') as HTMLElement;
      if (copyBtn) {
        navigator.clipboard.writeText(getCodeText(copyBtn));
        const saved = copyBtn.innerHTML;
        copyBtn.innerHTML = checkIconSvg;
        setTimeout(() => { copyBtn.innerHTML = saved; }, 2000);
        return;
      }
      const dlBtn = target.closest('.docs-chat-download-btn') as HTMLElement;
      if (dlBtn) {
        const text = getCodeText(dlBtn);
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'code.txt';
        a.click();
        URL.revokeObjectURL(url);
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, []);

  const validateFile = useCallback((file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !allowedExtensions.includes(ext)) {
      return 'Upload een PDF- of Word-bestand (.docx).';
    }
    if (file.size > maxFileSize) {
      return 'Bestand is te groot. Maximaal 10 MB.';
    }
    return null;
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const error = validateFile(file);
    if (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: error, responseType: 'markdown' }]);
      return;
    }
    setPendingFile(file);
  }, [validateFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const error = validateFile(file);
    if (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: error, responseType: 'markdown' }]);
      return;
    }
    setPendingFile(file);
  }, [validateFile]);

  const uploadFile = useCallback(async (file: File): Promise<string | null> => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${rootUrl}/api/form-generation/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload mislukt.' }));
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: error.error || 'Upload mislukt.',
          responseType: 'markdown'
        }]);
        return null;
      }

      const { sessionId: sid } = await response.json();
      lastUploadedFileRef.current = file;
      setSessionId(sid);
      return sid;
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Fout bij het uploaden van het bestand.',
        responseType: 'markdown'
      }]);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const question = input.trim();
    const connection = connectionRef.current;
    if (!question || isStreaming || isUploading || !connection) return;

    if (connection.state !== 'Connected') {
      try {
        await connection.start();
      } catch (err) {
        console.error('DocsHub connection error:', err);
        setSessionId(null);
        setPendingFile(null);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Kan geen verbinding maken met de server. Probeer het later opnieuw.',
          responseType: 'markdown'
        }]);
        return;
      }
    }

    // Upload pending file first
    let activeSessionId = sessionId;
    const file = pendingFile;
    if (file) {
      setPendingFile(null);
      activeSessionId = await uploadFile(file);
      if (!activeSessionId) return;
    }

    setIsStreaming(true);
    setInput('');
    setStreamingContent('');
    setStreamingType('markdown');

    const userMessage: DocsChatMessage = {
      role: 'user',
      content: question,
      ...(file ? { attachment: { fileName: file.name, sessionId: activeSessionId! } } : {})
    };

    const updatedMessages: DocsChatMessage[] = [...messages, userMessage];
    setMessages(updatedMessages);

    const history = messages.length > 0 ? messages.map(m => ({ role: m.role, content: m.content })) : null;

    const startStream = (sid: string | null, isRetry = false) => {
      let fullResponse = '';
      let responseType: 'markdown' | 'formJson' = 'markdown';
      let sessionExpired = false;

      const finalizeStream = (content?: string) => {
        if (content) {
          setMessages(prev => [...prev, { role: 'assistant', content, responseType }]);
          if (responseType === 'formJson') {
            setCurrentFormJson(content);
          }
        }
        setStreamingContent('');
        setIsStreaming(false);
        questionInputRef.current?.focus();
      };

      connection.stream('Ask', question, sid ?? null, currentFormJson ?? null, history, getPageContext(), getCulture())
        .subscribe({
          next: (item: DocsHubItem) => {
            if (item.type === 'error') {
              if (!isRetry && sid && item.value?.includes(SESSION_EXPIRED_ERROR)) {
                sessionExpired = true;
              } else {
                fullResponse = 'Er is een fout opgetreden. Probeer het later opnieuw.';
                setStreamingContent(fullResponse);
              }
              console.error('DocsHub error:', item.value);
            } else {
              if (item.type === 'formJson') {
                responseType = 'formJson';
                setStreamingType('formJson');
              }
              fullResponse += item.value;
              setStreamingContent(fullResponse);
            }
          },
          complete: async () => {
            if (sessionExpired) {
              const file = lastUploadedFileRef.current;
              if (file) {
                setStreamingContent('');
                const newSid = await uploadFile(file);
                if (newSid) {
                  startStream(newSid, true);
                  return;
                }
              }
              setSessionId(null);
              lastUploadedFileRef.current = null;
              finalizeStream('De sessie is verlopen. Upload het bestand opnieuw.');
              return;
            }

            const content = responseType === 'formJson' ? stripMarkdownFences(fullResponse) : fullResponse;
            finalizeStream(content);
          },
          error: (err: Error) => {
            if (fullResponse) {
              const content = responseType === 'formJson' ? stripMarkdownFences(fullResponse) : fullResponse;
              finalizeStream(content);
            } else {
              finalizeStream('De verbinding is verbroken. Probeer het opnieuw.');
            }
            console.error('Stream error:', err);
          }
        });
    };

    try {
      startStream(activeSessionId);
    } catch (err) {
      setIsStreaming(false);
      console.error('Stream error:', err);
    }
  }, [input, isStreaming, isUploading, messages, sessionId, currentFormJson, pendingFile, uploadFile]);

  const resetFormState = useCallback(() => {
    setCurrentFormJson(null);
    setSessionId(null);
    setPendingFile(null);
    lastUploadedFileRef.current = null;
  }, []);


  const renderMarkdown = (md: string): string => {
    try {
      return marked.parse(md)
        .replace(/<pre>/g,
          `<div class="docs-chat-code-wrapper">` +
          `<div class="docs-chat-code-icons">` +
          `<button class="docs-chat-copy-btn" title="Kopieer">${copyIconSvg}</button>` +
          `<button class="docs-chat-download-btn" title="Download">${downloadIconSvg}</button>` +
          `</div>` +
          `<pre class="docs-chat-code-block">`)
        .replace(/<\/pre>/g,
          `</pre>` +
          `<div class="docs-chat-code-actions">` +
          `<button class="docs-chat-copy-btn" title="Kopieer">${copyIconSvg} Kopieer</button>` +
          `<button class="docs-chat-download-btn" title="Download">${downloadIconSvg} Download</button>` +
          `</div>` +
          `</div>`);
    } catch {
      return md;
    }
  };

  const placeholder = currentFormJson
    ? 'Verfijn het formulier of stel een vraag...'
    : pendingFile
      ? 'Stel een vraag over het document of vraag om een formulier...'
      : 'Stel een vraag...';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="docs-chat-header">
        <span className="docs-chat-header-title">Documentatie Assistent</span>
        <div className="docs-chat-header-actions">
          {currentFormJson && (
            <button className="docs-chat-header-new-form" onClick={resetFormState}>Nieuw formulier</button>
          )}
          {messages.length > 0 && (
            <button onClick={() => { setMessages([]); resetFormState(); }} title="Nieuw gesprek">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="square" strokeLinejoin="miter" fillOpacity="0" strokeMiterlimit="4" stroke="currentColor" strokeWidth="2" d="M10,4 C8.136,4 7.204,4 6.469,4.304 C5.489,4.71 4.71,5.489 4.304,6.469 C4,7.204 4,8.136 4,10 L4,13.6 C4,15.84 4,16.96 4.436,17.816 C4.819,18.569 5.431,19.181 6.184,19.564 C7.04,20 8.16,20 10.4,20 L14,20 C15.864,20 16.796,20 17.531,19.696 C18.511,19.29 19.29,18.511 19.696,17.531 C20,16.796 20,15.864 20,14"/>
                <path strokeLinecap="square" strokeLinejoin="miter" fillOpacity="0" strokeMiterlimit="4" stroke="currentColor" strokeWidth="2" d="M19.499,7.501 L12.439,14.561 C12.159,14.841 11.777,15.001 11.379,15.001 L9,15.001 L9,12.621 C9,12.223 9.159,11.841 9.44,11.561 L16.5,4.501 C17.328,3.673 18.672,3.673 19.5,4.501 C20.328,5.329 20.328,6.673 19.499,7.501Z"/>
              </svg>
            </button>
          )}
          <button onClick={onToggleFullscreen} title={isFullscreen ? 'Verkleinen' : 'Vergroten'}>
            {isFullscreen
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            }
          </button>
          <button onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* Chat area */}
      <div ref={chatAreaRef} className="docs-chat-area">
        {messages.length === 0 && !streamingContent && (
          <div className="docs-chat-empty">
            <svg className="docs-chat-empty-illustration" width="130" height="100" viewBox="0 0 180 140" fill="none">
              <g transform="translate(30,25) rotate(-12)">
                <rect width="55" height="70" rx="2" fill="white" stroke="#dee2e6" strokeWidth="1"/>
                <line x1="10" y1="14" x2="42" y2="14" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="22" x2="38" y2="22" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="30" x2="35" y2="30" stroke="#e9ecef" strokeWidth="2"/>
                <line x1="10" y1="38" x2="40" y2="38" stroke="#e9ecef" strokeWidth="2"/>
              </g>
              <g transform="translate(100,20) rotate(8)">
                <rect width="55" height="70" rx="2" fill="white" stroke="#dee2e6" strokeWidth="1"/>
                <line x1="10" y1="14" x2="40" y2="14" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="22" x2="36" y2="22" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="30" x2="30" y2="30" stroke="#e9ecef" strokeWidth="2"/>
                <rect x="10" y="42" width="20" height="14" rx="1" fill="#e9ecef"/>
              </g>
              <g transform="translate(55,30) rotate(-2)">
                <defs><clipPath id="ear"><path d="M0,0 H43 L55,12 V70 H0 Z"/></clipPath></defs>
                <rect width="55" height="70" rx="2" fill="white" stroke="#cfd4da" strokeWidth="1" clipPath="url(#ear)"/>
                <rect x="10" y="10" width="28" height="4" rx="1" fill="#0d6efd" opacity="0.7"/>
                <line x1="10" y1="22" x2="42" y2="22" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="30" x2="40" y2="30" stroke="#cfe2ff" strokeWidth="2"/>
                <line x1="10" y1="38" x2="36" y2="38" stroke="#e9ecef" strokeWidth="2"/>
                <line x1="10" y1="46" x2="42" y2="46" stroke="#e9ecef" strokeWidth="2"/>
                <line x1="10" y1="54" x2="30" y2="54" stroke="#e9ecef" strokeWidth="2"/>
                <path d="M43,0 L55,0 L55,12 Z" fill="#e9ecef" stroke="#cfd4da" strokeWidth="0.75"/>
              </g>
            </svg>
            <span>Stel een vraag over de documentatie of sleep een document hierheen.</span>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i}>
            {msg.role === 'user' ? (
              <div className="docs-chat-bubble docs-chat-bubble-user">
                {msg.content}
                {msg.attachment && (
                  <div style={{ marginTop: '0.25rem' }}>
                    <span className="docs-chat-file-chip">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      {msg.attachment.fileName}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="docs-chat-bubble docs-chat-bubble-assistant">
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(
                  msg.responseType === 'formJson' ? '```json\n' + msg.content + '\n```' : msg.content
                ) }} />
              </div>
            )}
          </div>
        ))}

        {/* Streaming indicator */}
        {isStreaming && !streamingContent && !isUploading && (
          <div className="docs-chat-bubble docs-chat-bubble-assistant docs-chat-typing">
            <span className="docs-chat-dot" style={{ animationDelay: '0s' }} />
            <span className="docs-chat-dot" style={{ animationDelay: '0.2s' }} />
            <span className="docs-chat-dot" style={{ animationDelay: '0.4s' }} />
          </div>
        )}

        {/* Uploading indicator */}
        {isUploading && (
          <div className="docs-chat-bubble docs-chat-bubble-assistant docs-chat-uploading">
            <div className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px' }} />
            <span>Bestand verwerken...</span>
          </div>
        )}

        {/* Streaming content */}
        {streamingContent && (
          <div className="docs-chat-bubble docs-chat-bubble-assistant">
            <div dangerouslySetInnerHTML={{ __html: renderMarkdown(
              streamingType === 'formJson' ? '```json\n' + streamingContent + '\n```' : streamingContent
            ) }} />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div
        className="docs-chat-input-area"
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={e => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setIsDragging(false);
        }}
        onDrop={handleFileDrop}
      >
        {isDragging && (
          <div className="docs-chat-drop-overlay">
            Sleep een PDF of Word-bestand hierheen
          </div>
        )}

        {/* Pending file chip */}
        {pendingFile && (
          <div style={{ marginBottom: '0.4rem' }}>
            <span className="docs-chat-file-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              {pendingFile.name}
              <button onClick={() => setPendingFile(null)} title="Verwijder bestand">
                &times;
              </button>
            </span>
          </div>
        )}

        <div className="docs-chat-input-wrapper">
          {/* Paperclip button */}
          <button
            className="docs-chat-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming || isUploading}
            title="Bestand bijvoegen"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <input
            ref={questionInputRef}
            type="text"
            className="form-control form-control-sm docs-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
            placeholder={placeholder}
          />
          {input.trim() && !isStreaming && !isUploading && (
            <button className="docs-chat-send-btn" onClick={sendMessage} title="Verstuur">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
